/**
 * Every component gated behind useHydrated() (src/hooks/useHydrated.js), keyed here so
 * App.jsx can resolve them to plain component references BEFORE flipping the shared
 * hydrated signal - see preloadHydratedChunks() below for why they're no longer
 * rendered via lazy()+Suspense at all.
 */
const specs = {
  UserMenu: () => import('./components/UserMenu'),
  AuthModal: () => import('./components/auth/AuthModal'),
  InstructionsModal: () => import('./components/InstructionsModal'),
  VatInfoModal: () => import('./components/VatInfoModal'),
  TrackItemModal: () => import('./components/TrackItemModal'),
  MissingDomainsBanner: () => import('./components/banners/MissingDomainsBanner'),
  AmazonCommunityBanner: () => import('./components/banners/AmazonCommunityBanner'),
  AliExpressAdCard: () => import('./components/ItemCard/AliExpressAdCard'),
  ItemFullscreenModal: () => import('./components/ItemCard/ItemFullscreenModal'),
};

/**
 * Populated by preloadHydratedChunks() before markHydrated() ever fires - every call
 * site gated behind useHydrated() can safely read resolvedComponents.<Name> once
 * `hydrated` is true, since that's only possible after this has already resolved.
 */
export const resolvedComponents = {};

/**
 * On a /search/{slug} page, mounting these via lazy()+Suspense - even after
 * preloading their chunks (a prior fix, insufficient on its own) - still put ~40
 * Suspense boundaries (one ItemFullscreenModal per product card, plus every other
 * hydrated-gated modal/banner) through React's lazy-resolution retry path at once,
 * because lazy()'s internal promise handling always suspends on a component's first
 * render regardless of whether the underlying module was already cached - it still
 * needs a fresh microtask tick to flip its own internal status. Confirmed live via
 * DevTools breakpoints: singleton components that should mount exactly once (AuthModal,
 * InstructionsModal, VatInfoModal) were each being invoked ~5 times before the page
 * crashed - a real render-retry loop in that machinery, not just many product cards.
 *
 * Resolving these to plain component references here - and having every call site
 * render them directly instead of through lazy()/Suspense - removes React's lazy
 * retry path from the equation entirely for content gated behind hydration. By the
 * time `hydrated` can be true, every one of these is already a fully resolved value,
 * so mounting them is just ordinary reconciliation.
 */
export function preloadHydratedChunks() {
  return Promise.all(
    Object.entries(specs).map(([key, load]) =>
      load()
        .then((mod) => {
          resolvedComponents[key] = mod.default;
        })
        .catch(() => {})
    )
  );
}
